import { type ReactNode, useEffect, useState } from 'react'
import type { CitationPopover as Store, PopoverState } from '../field/controls/richtext/reference.js'
import { sanitizeCitationHtml } from './sanitizeHtml.js'

/**
 * De volledige bronvermelding bij een citatie in de tekst (R-50), naar CitatenPOC's popover in
 * `CitationNodeView.tsx`: de opgemaakte regel(s) uit de literatuurlijst van de bron(nen) van deze
 * citatie, live uit dezelfde uitkomst als het label, en nergens bewaard.
 *
 * **Toegankelijk, waar CitatenPOC alleen de muis kent** (R-139, R-158). Het tooltippatroon van
 * WAI-ARIA: `role="tooltip"`, de citatie wijst ernaar met `aria-describedby`, en Escape sluit hem.
 * Hij opent bij de muis, bij een citatie die met een pijltoets is gekozen, en in een alleen-lezen
 * veld bij focus (`richtext/reference.ts`). In een tekstvak leest een schermlezer een beschrijving
 * niet voor als de cursor op een knoop springt; daarom staat de tekst bij een keuze met het
 * toetsenbord ook in een live region.
 *
 * Gesaniteerd vóór weergave, om dezelfde reden als in `ReferenceList`.
 */
export function CitationPopover({
  store,
  id,
  entries,
}: {
  store: Store
  id: string
  entries: ReadonlyMap<string, readonly string[]>
}): ReactNode {
  const [state, setState] = useState<PopoverState | null>(store.current())
  useEffect(() => {
    setState(store.current())
    return store.subscribe(() => setState(store.current()))
  }, [store])

  // Escape sluit, waar de focus ook staat (WAI-ARIA tooltip).
  useEffect(() => {
    if (state === null) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') store.close()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [state, store])

  const lines = state === null ? [] : (entries.get(state.citationId) ?? [])
  const open = state !== null && lines.length > 0
  const rect = open ? state.anchor.getBoundingClientRect() : undefined
  const spoken = open && state.via === 'selectie' ? lines.map(textOf).join(' ') : ''

  return (
    <>
      {open && rect !== undefined ? (
        <div
          role="tooltip"
          id={id}
          className="ef-referentie-popover"
          style={{ left: rect.left, top: rect.bottom }}
        >
          {lines.map((html, i) => (
            <div
              key={i}
              className="ef-referentie-popover__regel"
              // eslint-disable-next-line react/no-danger -- gesaniteerd, zie de kop hierboven
              dangerouslySetInnerHTML={{ __html: sanitizeCitationHtml(html) }}
            />
          ))}
        </div>
      ) : null}
      <span className="ef-sr-only ef-referentie-voorlezen" aria-live="polite">
        {spoken}
      </span>
    </>
  )
}

function textOf(html: string): string {
  const element = document.createElement('div')
  element.innerHTML = sanitizeCitationHtml(html)
  return element.textContent?.trim() ?? ''
}
